import { getProductWizardRoute } from '@/app/config/products.config';
import {
  canCloneGroupLifeQuote,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { useDataTablePageSync, useRequirePermissions } from '@/shared/hooks';
import {
  Avatar,
  DataTable,
  DataTableColumn,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { CalendarDays, Copy, Edit, Eye, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Broker } from '../../../services/brokers/types';
import type { SummaryQuotes } from '../../../services/quotes/types';
import {
  DateRangeFilterField,
  SearchFilterField,
  SelectFilterField,
} from '../../../shared/ui';
import { Page, PageHeader } from '../../../shared/ui/page';
import { toaster } from '../../../shared/ui/toaster.component';
import { f, formatDateToBrazil, useFilters } from '../../../shared/utils';
import { isClonedQuoteMarkedAsDraft } from '../../../shared/utils/clone-quote-status.util';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import {
  isCapitalGlobalQuoteFromSummary,
  resolveCapitalGlobalQuoteListActions,
} from './capital-global/capital-global-quote-product.util';
import {
  useBrokerProducts,
  useSelectedBrokerWriteGuard,
} from '../../brokers/hooks';
import type { BrokerWriteGuard } from '../../brokers/utils/broker-status.util';
import { useCopyQuote } from '../hooks/common/use-copy-quote.hook';
import { useCopyGroupLifeQuote } from '../hooks/group-life/use-copy-quote.hook';
import { GroupLifeClonePendingError } from '@/shared/utils/group-life-clone-idempotency.util';
import { useProducts } from '../hooks/common/use-products.hook';
import { useQuoteStatusCounts } from '../hooks/common/use-quote-status-counts.hook';
import { useQuotes } from '../hooks/common/use-quotes.hook';
import { CloneQuoteDialog } from './clone-quote.dialog';
import { QuoteStatusCards } from './quote-status-cards.component';

const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Em digitação', value: '1' },
  { label: 'Encerrada', value: '2' },
  { label: 'Calculada', value: '3' },
  { label: 'Expirada', value: '4' },
  { label: 'Excluída', value: '5' },
  { label: 'Cotação em Análise', value: '17' },
];

const DEFAULT_SORT_FIELD = 'index_time';
const DEFAULT_SORT_TYPE = 'DESC';

const QUOTE_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  insuredName: 'Nome',
  registerNumber: 'CPF',
  brokerName: 'AgentName',
  productName: 'ProductName',
  id: 'QuotationNo',
};

const QUOTE_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  Nome: 'insuredName',
  CPF: 'registerNumber',
  AgentName: 'brokerName',
  ProductName: 'productName',
  QuotationNo: 'id',
};

const getStatusConfig = (status: number) => {
  const statusMap: Record<
    number,
    {
      label: string;
      colorScheme: 'gray' | 'yellow' | 'success' | 'error' | 'blue';
    }
  > = {
    [PortalStatus.QuoteInProgress]: {
      label: 'Em digitação',
      colorScheme: 'gray',
    },
    [PortalStatus.QuoteClosed]: { label: 'Encerrada', colorScheme: 'error' },
    [PortalStatus.QuoteCalculated]: {
      label: 'Calculada',
      colorScheme: 'blue',
    },
    [PortalStatus.QuoteExpired]: { label: 'Expirada', colorScheme: 'error' },
    [PortalStatus.QuoteDeleted]: { label: 'Excluída', colorScheme: 'gray' },
    [PortalStatus.QuoteUnderAnalysis]: {
      label: 'Cotação em Análise',
      colorScheme: 'yellow',
    },
  };
  return statusMap[status] || { label: 'Desconhecido', colorScheme: 'gray' };
};

function isGroupLifeQuoteFromSummary(
  quote: Pick<SummaryQuotes, 'productName'>
): boolean {
  const name = (quote.productName || '').toLowerCase();
  return name.includes('pme') || name.includes('vida em grupo');
}

function resolveQuoteListStatus(quote: SummaryQuotes) {
  // A marca local "clone draft" só vale enquanto a cotação está realmente
  // em digitação ou recém-calculada (caso do clone). Para status terminais
  // ou em análise no Insuremo, o status real do backend prevalece.
  const ignoresDraftMark =
    quote.proposalStatus === PortalStatus.QuoteClosed ||
    quote.proposalStatus === PortalStatus.QuoteExpired ||
    quote.proposalStatus === PortalStatus.QuoteDeleted ||
    quote.proposalStatus === PortalStatus.QuoteUnderAnalysis;

  if (!ignoresDraftMark && isClonedQuoteMarkedAsDraft(quote.policyId)) {
    return PortalStatus.QuoteInProgress;
  }

  return quote.proposalStatus;
}

/**
 * Rota do wizard para cotação de produto com backend próprio (registry), ou
 * `undefined` para cotação InsureMO. `flow='view'` abre em modo somente
 * leitura (paridade com o "Ver detalhes" dos demais produtos); `flow='new'`
 * mantém o wizard editável ("Editar").
 */
function quoteWizardRoute(
  quote: SummaryQuotes,
  resolvedStatus: number,
  flow: 'new' | 'view' = 'new'
) {
  if (!quote.policyId) return undefined;
  return getProductWizardRoute(
    quote.productCode,
    quote.policyId,
    resolvedStatus,
    flow
  );
}

function createColumns(
  navigate: ReturnType<typeof useNavigate>,
  // eslint-disable-next-line no-unused-vars
  onClone: (_quote: SummaryQuotes) => void,
  canWrite: boolean,
  writeGuard: BrokerWriteGuard
): DataTableColumn<SummaryQuotes>[] {
  return [
    {
      accessorKey: 'insuredName',
      header: 'PROPONENTE',
      size: 200,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
              <User className='w-4 h-4 text-white' />
            </Avatar.Root>
            <div className='flex flex-col gap-1'>
              <span className='font-gotham font-bold text-alba-black-500'>
                {quote.insuredName}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'registerNumber',
      header: 'CPF/CNPJ',
      size: 200,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <span className='text-alba-gray-700 text-sm'>
            {formatRegisterNumber(quote.registerNumber)}
          </span>
        );
      },
    },
    {
      accessorKey: 'brokerName',
      header: 'CORRETOR',
      size: 200,
      cell: ({ row }) => {
        const quote = row.original;
        const brokerName = quote.brokerName
          ? quote.brokerName.split('\n')[0]
          : '-';
        return <span className='text-alba-gray-700 text-sm'>{brokerName}</span>;
      },
    },
    {
      accessorKey: 'productName',
      header: 'PRODUTO',
      size: 200,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <span className='text-alba-gray-700 text-sm'>
            {quote.productName}
          </span>
        );
      },
    },
    {
      accessorKey: 'id',
      header: 'Nº DA COTAÇÃO',
      size: 400,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <div className='flex flex-col gap-1'>
            <span className='text-alba-gray-700 text-sm font-medium'>
              {quote.proposalNumber}
            </span>
            <div className='flex items-center gap-1 text-alba-gray-600 text-xs'>
              <CalendarDays className='w-4 h-4 text-alba-gray-600 mb-0.5' />
              <span>{formatDateToBrazil(quote.proposalDate)}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'inclusionUserName',
      header: 'CRIADO POR',
      size: 150,
      sortable: false,
      cell: ({ row }) => {
        const quote = row.original;
        return (
          <span className='text-alba-gray-700 text-sm'>
            {quote.inclusionUserName || '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'proposalStatus',
      header: 'STATUS',
      size: 150,
      sortable: false,
      cell: ({ row }) => {
        const quote = row.original;
        const resolvedStatus = resolveQuoteListStatus(quote);
        const config = getStatusConfig(resolvedStatus);

        return (
          <Tag.Root
            variant='outline'
            colorScheme={config.colorScheme}
            size='sm'
          >
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 50,
      sortable: false,
      cell: ({ row }) => {
        const quote = row.original;
        const resolvedStatus = resolveQuoteListStatus(quote);
        const editWizardRoute = quoteWizardRoute(quote, resolvedStatus);
        const viewWizardRoute = quoteWizardRoute(quote, resolvedStatus, 'view');
        const isCapitalGlobal = isCapitalGlobalQuoteFromSummary(quote);
        const isGroupLife = isGroupLifeQuoteFromSummary(quote);
        const capitalGlobalActions = isCapitalGlobal
          ? resolveCapitalGlobalQuoteListActions(resolvedStatus)
          : null;
        const canEdit = capitalGlobalActions
          ? capitalGlobalActions.canEdit
          : resolvedStatus === PortalStatus.QuoteInProgress;
        const canViewDetails = capitalGlobalActions
          ? capitalGlobalActions.canViewDetails
          : !(isGroupLife && resolvedStatus === PortalStatus.QuoteInProgress);
        // Produto de backend próprio não clona; só cotações InsureMO clonam.
        const canClone =
          !editWizardRoute &&
          (capitalGlobalActions
            ? capitalGlobalActions.canClone
            : isGroupLife
              ? canCloneGroupLifeQuote(resolvedStatus)
              : resolvedStatus === PortalStatus.QuoteInProgress ||
                resolvedStatus === PortalStatus.QuoteCalculated);

        const handleEdit = () => {
          const resolvedPolicyId = quote.policyId;
          if (!resolvedPolicyId) return;
          if (editWizardRoute) {
            navigate(editWizardRoute);
            return;
          }
          navigate({
            to: '/quotes/$policyId/edit',
            params: { policyId: resolvedPolicyId },
            search: { mode: 'edit' },
          });
        };

        const handleViewDetails = () => {
          const resolvedPolicyId = quote.policyId;
          if (!resolvedPolicyId) return;
          if (viewWizardRoute) {
            navigate(viewWizardRoute);
            return;
          }
          navigate({
            to: '/quotes/$policyId',
            params: { policyId: resolvedPolicyId },
          });
        };

        const handleClone = () => {
          onClone(quote);
        };

        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                {canViewDetails && (
                  <Menu.Item
                    value='view'
                    icon={<Eye size={16} />}
                    onSelect={handleViewDetails}
                  >
                    Ver detalhes
                  </Menu.Item>
                )}
                {canWrite && canEdit && (
                  <Menu.Item
                    value='edit'
                    icon={<Edit size={16} />}
                    onSelect={handleEdit}
                    disabled={writeGuard.blocked}
                    title={writeGuard.reason}
                  >
                    Editar
                  </Menu.Item>
                )}
                {canWrite && canClone && (
                  <Menu.Item
                    value='clone'
                    icon={<Copy size={16} />}
                    onSelect={handleClone}
                    disabled={writeGuard.blocked}
                    title={writeGuard.reason}
                  >
                    Clonar Cotação
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}

export function QuotesContent({
  selectedBroker,
}: {
  selectedBroker: Broker | null;
}) {
  const navigate = useNavigate();
  const { hasPermission: canWrite } = useRequirePermissions({
    permissions: ['policies.quotes.write'],
  });
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [quoteToClone, setQuoteToClone] = useState<SummaryQuotes | null>(null);
  // Com corretora selecionada todas as linhas são dela; em "Todos Corretores"
  // o guard não trava e a Api barra por linha (403).
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const copyQuoteMutation = useCopyQuote();
  const copyGroupLifeMutation = useCopyGroupLifeQuote();
  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    dateFrom: f.string().withDefault(''),
    dateTo: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });
  const dataTableRef = useDataTablePageSync<SummaryQuotes>(filters.pageNo);

  const { data: brokerProducts } = useBrokerProducts(selectedBroker?.code);
  const { data: allProducts } = useProducts();

  // Com um corretor selecionado, exibe apenas os produtos ativos daquele
  // corretor. Sem corretor ("Todos Corretores"), exibe todos os produtos ativos.
  const productItems = selectedBroker
    ? (brokerProducts?.data ?? [])
        .filter(product => product.status === 'active')
        .map(product => ({
          label: product.name,
          value: product.code,
        }))
    : (allProducts ?? []).map(product => ({
        label: product.name,
        value: product.code,
      }));

  const productOptions = [
    { label: 'Todos os produtos', value: '' },
    ...productItems,
  ];

  const { counts: statusCounts, isLoading: isLoadingCounts } =
    useQuoteStatusCounts({
      partyCode: selectedBroker?.code,
      productCode: debouncedFilters.productCode || undefined,
      proposalDateFrom: debouncedFilters.dateFrom || undefined,
      proposalDateTo: debouncedFilters.dateTo || undefined,
    });

  // Card e dropdown compartilham o filtro `status` (mutuamente exclusivos);
  // o card ativo é derivado de `filters.status`.
  const handleSelectStatusCard = (status: string | null) => {
    setFilters({ ...filters, status: status ?? '', pageNo: 1 });
  };

  const {
    data: quotes,
    isLoading,
    error,
  } = useQuotes({
    pageNumber: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    sortType: filters.sortType === 'DESC' ? 'DESC' : 'ASC',
    sortField: filters.sortField,
    search: debouncedFilters.search,
    status: debouncedFilters.status,
    productCode: debouncedFilters.productCode,
    proposalDateFrom: debouncedFilters.dateFrom || undefined,
    proposalDateTo: debouncedFilters.dateTo || undefined,
    partyCode: selectedBroker?.code,
  });

  const columns = useMemo(
    () =>
      createColumns(
        navigate,
        quote => {
          setQuoteToClone(quote);
          setIsCloneDialogOpen(true);
        },
        canWrite ?? false,
        quoteWriteGuard
      ),
    [navigate, canWrite, quoteWriteGuard]
  );
  const sorting = useMemo(() => {
    const columnId = QUOTE_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'DESC',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  const isCloning =
    copyQuoteMutation.isPending || copyGroupLifeMutation.isPending;

  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
    setQuoteToClone(null);
    copyQuoteMutation.reset();
    copyGroupLifeMutation.reset();
  };

  const handleConfirmClone = async () => {
    const policyId = quoteToClone?.policyId;
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Clonar cotação',
        description: 'Não foi possível identificar a cotação para clonagem.',
      });
      handleCloseCloneDialog();
      return;
    }

    try {
      const { policyId: newPolicyId } = isGroupLifeQuoteFromSummary(
        quoteToClone
      )
        ? await copyGroupLifeMutation.mutateAsync(policyId)
        : await copyQuoteMutation.mutateAsync({
            policyId,
            quoteNumber: quoteToClone?.proposalNumber,
          });

      toaster.create({
        type: 'success',
        title: 'Clonar cotação',
        description: 'Cotação clonada com sucesso.',
      });

      handleCloseCloneDialog();
      navigate({
        to: '/quotes/$policyId/edit',
        params: { policyId: newPolicyId },
        search: { mode: 'edit' },
      });
    } catch (error) {
      // O clone pode ter sido concluído sem a resposta chegar: fecha o dialog
      // para o corretor conferir a lista em vez de confirmar de novo.
      if (error instanceof GroupLifeClonePendingError) {
        handleCloseCloneDialog();
        toaster.create({
          type: 'info',
          title: 'Clonar cotação',
          description: error.message,
        });
        return;
      }
      toaster.create({
        type: 'error',
        title: 'Clonar cotação',
        description:
          error instanceof Error
            ? error.message
            : 'Erro ao clonar cotação. Tente novamente.',
      });
    }
  };

  return (
    <>
      <div className='pt-4'>
        <QuoteStatusCards
          counts={statusCounts}
          activeStatus={filters.status || null}
          onSelectStatus={handleSelectStatusCard}
          isLoading={isLoadingCounts}
        />
      </div>

      <PageHeader.Filters>
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4 lg:max-w-4xl'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
            className='w-full'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, status: value, pageNo: 1 })
            }
          />
          <SelectFilterField
            {...registerFilter('productCode')}
            items={productOptions}
            placeholder='Produto'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, productCode: value, pageNo: 1 })
            }
          />
          <DateRangeFilterField
            from={filters.dateFrom}
            to={filters.dateTo}
            placeholder='Data da cotação'
            className='w-full md:min-w-[200px]'
            onChange={range =>
              setFilters({
                ...filters,
                dateFrom: range.from,
                dateTo: range.to,
                pageNo: 1,
              })
            }
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar cotações. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-3 md:hidden'>
              {isLoading ? (
                Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                  (_, index) => (
                    <div
                      key={index}
                      className='h-44 animate-pulse rounded-2xl bg-alba-gray-200'
                    />
                  )
                )
              ) : (quotes?.data ?? []).length === 0 ? (
                <div className='rounded-2xl bg-white p-6 text-center text-sm text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhuma cotação encontrada.
                </div>
              ) : (
                (quotes?.data ?? []).map(quote => {
                  const resolvedStatus = resolveQuoteListStatus(quote);
                  const statusConfig = getStatusConfig(resolvedStatus);
                  const editWizardRoute = quoteWizardRoute(
                    quote,
                    resolvedStatus
                  );
                  const viewWizardRoute = quoteWizardRoute(
                    quote,
                    resolvedStatus,
                    'view'
                  );
                  const isCapitalGlobal =
                    isCapitalGlobalQuoteFromSummary(quote);
                  const isGroupLife = isGroupLifeQuoteFromSummary(quote);
                  const capitalGlobalActions = isCapitalGlobal
                    ? resolveCapitalGlobalQuoteListActions(resolvedStatus)
                    : null;
                  const canEdit = capitalGlobalActions
                    ? capitalGlobalActions.canEdit
                    : resolvedStatus === PortalStatus.QuoteInProgress;
                  const canViewDetails = capitalGlobalActions
                    ? capitalGlobalActions.canViewDetails
                    : !(
                        isGroupLife &&
                        resolvedStatus === PortalStatus.QuoteInProgress
                      );
                  // Produto de backend próprio não clona.
                  const canClone =
                    !editWizardRoute &&
                    (capitalGlobalActions
                      ? capitalGlobalActions.canClone
                      : isGroupLife
                        ? canCloneGroupLifeQuote(resolvedStatus)
                        : resolvedStatus === PortalStatus.QuoteInProgress ||
                          resolvedStatus === PortalStatus.QuoteCalculated);
                  const brokerName = quote.brokerName
                    ? quote.brokerName.split('\n')[0]
                    : '-';

                  const handleViewDetails = () => {
                    if (!quote.policyId) return;
                    if (viewWizardRoute) {
                      navigate(viewWizardRoute);
                      return;
                    }
                    navigate({
                      to: '/quotes/$policyId',
                      params: { policyId: quote.policyId },
                    });
                  };

                  const handleEdit = () => {
                    if (!quote.policyId) return;
                    if (editWizardRoute) {
                      navigate(editWizardRoute);
                      return;
                    }
                    navigate({
                      to: '/quotes/$policyId/edit',
                      params: { policyId: quote.policyId },
                      search: { mode: 'edit' },
                    });
                  };

                  const handleClone = () => {
                    setQuoteToClone(quote);
                    setIsCloneDialogOpen(true);
                  };

                  return (
                    <article
                      key={quote.policyId || quote.proposalNumber}
                      className='rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex min-w-0 items-center gap-3'>
                          <Avatar.Root
                            size='md'
                            shape='circle'
                            className='bg-alba-blue-500'
                          >
                            <User className='h-4 w-4 text-white' />
                          </Avatar.Root>
                          <div className='min-w-0'>
                            <p className='truncate font-gotham font-bold text-alba-black-500'>
                              {quote.insuredName}
                            </p>
                            <p className='mt-1 text-xs text-alba-gray-600'>
                              {formatRegisterNumber(quote.registerNumber)}
                            </p>
                          </div>
                        </div>
                        <Menu.Root>
                          <Menu.Trigger />
                          <Menu.Content>
                            {canViewDetails && (
                              <Menu.Item
                                value='view'
                                icon={<Eye size={16} />}
                                onSelect={handleViewDetails}
                              >
                                Ver detalhes
                              </Menu.Item>
                            )}
                            {canWrite && canEdit && (
                              <Menu.Item
                                value='edit'
                                icon={<Edit size={16} />}
                                onSelect={handleEdit}
                                disabled={quoteWriteGuard.blocked}
                                title={quoteWriteGuard.reason}
                              >
                                Editar
                              </Menu.Item>
                            )}
                            {canWrite && canClone && (
                              <Menu.Item
                                value='clone'
                                icon={<Copy size={16} />}
                                onSelect={handleClone}
                                disabled={quoteWriteGuard.blocked}
                                title={quoteWriteGuard.reason}
                              >
                                Clonar Cotação
                              </Menu.Item>
                            )}
                          </Menu.Content>
                        </Menu.Root>
                      </div>

                      <div className='mt-4 h-px w-full bg-alba-gray-200' />

                      <div className='mt-4 space-y-2'>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Corretor
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {brokerName}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Produto
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {quote.productName}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Nº da Cotação
                          </p>
                          <div className='flex flex-col items-end gap-0.5 text-right'>
                            <p className='text-sm text-alba-gray-700'>
                              {quote.proposalNumber}
                            </p>
                            <div className='flex items-center gap-1 text-xs text-alba-gray-600'>
                              <CalendarDays className='h-3.5 w-3.5' />
                              <span>
                                {formatDateToBrazil(quote.proposalDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Criado por
                          </p>
                          <p className='truncate text-sm text-alba-gray-700'>
                            {quote.inclusionUserName || '-'}
                          </p>
                        </div>
                        <div className='flex items-center justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Status
                          </p>
                          <Tag.Root
                            variant='outline'
                            colorScheme={statusConfig.colorScheme}
                            size='sm'
                          >
                            <Tag.Label>{statusConfig.label}</Tag.Label>
                          </Tag.Root>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}

              {!isLoading && (quotes?.meta?.totalPages ?? 0) > 0 && (
                <div className='flex justify-center pt-2'>
                  <Pagination
                    count={(quotes?.meta?.totalPages ?? 0) * filters.pageSize}
                    page={filters.pageNo}
                    pageSize={filters.pageSize}
                    totalItems={quotes?.meta?.total ?? 0}
                    pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        pageNo: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, pageNo: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden md:block'>
              <DataTable
                ref={dataTableRef}
                loading={isLoading}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                totalPages={quotes?.meta?.totalPages || 0}
                data={quotes?.data || []}
                pageSize={filters.pageSize}
                totalItems={quotes?.meta?.total || 0}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? QUOTE_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                    : DEFAULT_SORT_FIELD;

                  setFilters({
                    ...filters,
                    pageNo: 1,
                    sortField: sortField ?? DEFAULT_SORT_FIELD,
                    sortType: nextSort?.desc ? 'DESC' : 'ASC',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, pageNo: page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>

      <CloneQuoteDialog
        isOpen={isCloneDialogOpen && Boolean(quoteToClone)}
        isLoading={isCloning}
        onClose={handleCloseCloneDialog}
        onConfirm={handleConfirmClone}
      />
    </>
  );
}
